import React, { useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Link,
  Tooltip,
  CircularProgress,
  Button,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { green, red } from "@mui/material/colors";
import { resultsAtom, loadingResultsAtom } from "../state/atoms";
import { useAtom } from "jotai";
import { cloneDeep } from "lodash";

const PublicationsTable = ({ publications, registration, buildQueryString }) => {
  const [results, setResults] = useAtom(resultsAtom);
  const [loadingResults, setLoadingResults] = useAtom(loadingResultsAtom);
  const { trialId } = registration;
  const fetchedRef = useRef(new Set());

  // Export to CSV function
  const exportToCsv = () => {
    if (!trialId) return;
    const headers = [
      "PMID",
      "Title",
      "Authors",
      "Abstract",
      "Source(s)",
      "Reports results?",
      "LLM Reasoning",
    ];
    const rows = publications.map((pub) => {
      const res = results[trialId]?.[pub.pmid];
      return [
        pub.pmid,
        pub.title?.replace(/"/g, '""') || "",
        pub.authors?.replace(/"/g, '""') || "",
        pub.abstract?.replace(/"/g, '""') || "",
        pub.sources?.join(", ") || "",
        res?.hasResults ? "Yes" : res ? "No" : "Unknown",
        res?.reason?.replace(/"/g, '""') || "",
      ];
    });
    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((value) => `"${value}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `${trialId}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fetchResult = (pmid) => {
    setLoadingResults((prev) => {
      const next = cloneDeep(prev);
      if (!next[trialId]) next[trialId] = {};
      next[trialId][pmid] = true;
      return next;
    });

    const qs = buildQueryString ? buildQueryString() : "";
    fetch(`/api/results/${trialId}/${pmid}${qs}`)
      .then((response) => response.json())
      .then((data) => {
        console.log({ data });
        if (!data?.success) {
          throw new Error(`Search for results failed: ${data?.error}`);
        }
        setResults((prev) => {
          const newResults = cloneDeep(prev);
          if (!newResults[trialId]) {
            newResults[trialId] = {};
          }
          newResults[trialId][pmid] = data?.content;
          return newResults;
        });
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoadingResults((prev) => {
          const newLoadingResults = cloneDeep(prev);
          if (!newLoadingResults[trialId]) {
            newLoadingResults[trialId] = {};
          }
          newLoadingResults[trialId][pmid] = false;
          return newLoadingResults;
        });
      });
  };

  useEffect(() => {
    if (!trialId || !publications.length) return;

    publications.slice(0, 15).forEach((publication) => {
      const pmid = publication?.pmid;
      if (!pmid) return;

      const key = `${trialId}:${pmid}`;
      if (fetchedRef.current.has(key)) return;
      if (results[trialId]?.[pmid]) return;

      fetchedRef.current.add(key);
      fetchResult(pmid);
    });
  }, [trialId, publications]);

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{ marginTop: "2rem", alignSelf: "center", overflowX: "auto" }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  resize: "horizontal",
                  overflow: "auto",
                  minWidth: "120px",
                }}
              >
                PMID
              </TableCell>
              <TableCell
                sx={{
                  resize: "horizontal",
                  overflow: "auto",
                  minWidth: "120px",
                }}
              >
                Title
              </TableCell>
              <TableCell
                sx={{
                  resize: "horizontal",
                  overflow: "auto",
                  minWidth: "120px",
                }}
              >
                Authors
              </TableCell>
              <TableCell
                sx={{
                  resize: "horizontal",
                  overflow: "auto",
                  minWidth: "120px",
                }}
              >
                Abstract
              </TableCell>

              <TableCell
                sx={{
                  resize: "horizontal",
                  overflow: "auto",
                  minWidth: "120px",
                }}
              >
                Source(s)
              </TableCell>

              <TableCell
                sx={{
                  resize: "horizontal",
                  overflow: "auto",
                  minWidth: "120px",
                }}
              >
                Reports results?
              </TableCell>
              <TableCell
                sx={{
                  resize: "horizontal",
                  overflow: "auto",
                  minWidth: "120px",
                }}
              >
                LLM Reasoning
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {publications.map((publication, index) => (
              <TableRow key={index}>
                {/* PMID with Link */}
                <TableCell>
                  <Link
                    href={`https://pubmed.ncbi.nlm.nih.gov/${publication.pmid}/`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {publication.pmid}
                  </Link>
                </TableCell>

                {/* Title with Tooltip */}
                <TableCell>
                  <Tooltip
                    title={publication.title || "N/A"}
                    placement="top"
                    arrow
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        WebkitBoxAlign: "start",
                        whiteSpace: "normal",
                        width: "100%",
                      }}
                    >
                      {publication.title || "N/A"}
                    </Typography>
                  </Tooltip>
                </TableCell>

                {/* Authors with Tooltip */}
                <TableCell>
                  <Tooltip
                    title={publication.authors || "N/A"}
                    placement="top"
                    arrow
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        WebkitBoxAlign: "start",
                        whiteSpace: "normal",
                        width: "100%",
                      }}
                    >
                      {publication.authors || "N/A"}
                    </Typography>
                  </Tooltip>
                </TableCell>

                {/* Abstract with Tooltip */}
                <TableCell>
                  <Tooltip
                    title={publication.abstract || "No abstract available"}
                    placement="top"
                    arrow
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        WebkitBoxAlign: "start",
                        whiteSpace: "normal",
                        width: "100%",
                      }}
                    >
                      {publication.abstract || "No abstract available"}
                    </Typography>
                  </Tooltip>
                </TableCell>

                {/* Source(s) */}
                <TableCell>{publication.sources?.join(", ")}</TableCell>

                {/* Results Status */}
                <TableCell align="center">
                  {loadingResults[trialId]?.[publication?.pmid] ? (
                    <CircularProgress size={20} />
                  ) : results[trialId]?.[publication?.pmid] ? (
                    results[trialId][publication.pmid].hasResults ? (
                      <CheckCircleIcon sx={{ color: green[500] }} />
                    ) : (
                      <CancelIcon sx={{ color: red[500] }} />
                    )
                  ) : index >= 15 ? (
                    <Button
                      onClick={() => fetchResult(publication?.pmid)}
                      sx={{ margin: 1 }}
                      variant="contained"
                    >
                      Search
                    </Button>
                  ) : (
                    "Unknown"
                  )}
                </TableCell>

                {/* Reason with Tooltip */}
                <TableCell>
                  <Tooltip
                    title={results[trialId]?.[publication?.pmid]?.reason}
                    placement="top"
                    arrow
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        WebkitBoxAlign: "start",
                        whiteSpace: "normal",
                        width: "100%",
                      }}
                    >
                      {results[trialId]?.[publication?.pmid]?.reason}
                    </Typography>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Button
        variant="contained"
        sx={{ marginTop: 4 }}
        onClick={exportToCsv}
        startIcon={<SaveAltIcon />}
        disabled={
          publications.some((pub) => loadingResults[trialId]?.[pub.pmid]) ||
          publications.some((pub) => results[trialId]?.[pub.pmid] == null)
        }
      >
        Export Results
      </Button>
    </>
  );
};

export default PublicationsTable;
